import React, { useState, useEffect, Fragment } from "react";
import {
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PremiumCard from "../../ui/PremiumCard";
import Form from "../../Form";

// Sub-formulários legados e validações
import NomeTarefa from "../Formulario/NomeTarefa";
import GrupoTarefa from "../Formulario/GrupoTarefa";
import FormTipoTarefa from "../Formulario/TipoTarefa";
import BackupBancoDados from "../Formulario/Definicao/BackupBancoDados";
import BackupArquivos from "../Formulario/Definicao/BackupArquivos";
import BackupBat from "../Formulario/Definicao/BackupBat";
import Agendamento from "../Formulario/Agendamento";
import PosExecucaoForm from "../Formulario/Definicao/PosExecucao";
import {
  validarAgendamento,
  validarDefinicao,
  validarNome,
  validarTipo,
  validarGrupo,
  validarPosExecucao
} from "../Formulario/validacoes";

import { TarefaProvider, useTarefa, novaTarefa } from "../../../contexts/TarefaContext";
import { useLogin } from "../../../contexts/LoginContext";
import { useCliente } from "../../../contexts/ClienteContext";
import { TarefasApi } from "../services/tarefasApi";
import { IBackupTarefaV2 } from "../types";
import { ITarefa } from "../../../types/tarefa";
import { formatarDataHoraCurta } from "../../../common/utils/formatadorDataHora";
import { ModalInstrucoesLinux } from "../ModalInstrucoesLinux";

interface IWizardTarefaProps {
  tarefaParaEditar: IBackupTarefaV2 | null;
  onSave: () => void;
  onCancel: () => void;
}

interface IFormStep {
  titulo: string;
  validar: (tarefa: ITarefa) => boolean;
  criarForm: (tarefa: ITarefa) => JSX.Element;
}

const criarFormularioDefinicaoTarefa = (tarefa: ITarefa) => {
  if (tarefa.tipo === "SQLServer" || tarefa.tipo === "Sybase" || tarefa.tipo === "Postgres" || tarefa.tipo === "CheckDB") {
    return <BackupBancoDados />;
  } else if (tarefa.tipo === "Arquivos") {
    return <BackupArquivos />;
  } else if (tarefa.tipo === "Bat") {
    return <BackupBat />;
  }
  return <Typography>Selecione um tipo de backup primeiro.</Typography>;
};

const forms: IFormStep[] = [
  {
    titulo: "Nome",
    validar: validarNome,
    criarForm: () => <NomeTarefa />
  },
  {
    titulo: "Grupo",
    validar: validarGrupo,
    criarForm: () => <GrupoTarefa />
  },
  {
    titulo: "Tipo de Origem",
    validar: validarTipo,
    criarForm: () => <FormTipoTarefa />
  },
  {
    titulo: "Configuração",
    validar: validarDefinicao,
    criarForm: (tarefa) => criarFormularioDefinicaoTarefa(tarefa)
  },
  {
    titulo: "Agendamento",
    validar: validarAgendamento,
    criarForm: () => <Agendamento />
  },
  {
    titulo: "Pós-Execução",
    validar: validarPosExecucao,
    criarForm: () => <PosExecucaoForm />
  }
];

const mapearV2ParaTarefa = (v2: IBackupTarefaV2): ITarefa => {
  return {
    id: v2.tarefaAgendadaID || v2.id,
    clienteID: v2.clienteID,
    nome: v2.nome,
    tipo: v2.tipo,
    plataforma: (v2.plataforma as any) || "windows",
    dataInicio: v2.dataInicio ? new Date(v2.dataInicio) : new Date(),
    dataFim: v2.dataFim ? new Date(v2.dataFim) : new Date("2200-12-31"),
    ultimaExecucao: v2.ultimaExecucao ? new Date(v2.ultimaExecucao.dataHora) : undefined,
    configBancoDados: v2.configBancoDados
      ? {
          id: v2.configBancoDados.id || "",
          tipo: v2.configBancoDados.tipo || "",
          servidor: v2.configBancoDados.servidor || "",
          bancoDados: v2.configBancoDados.bancoDados || "",
          porta: v2.configBancoDados.porta || 1433,
          usuario: v2.configBancoDados.usuario || "",
          senha: v2.configBancoDados.senha || "",
          descricao: v2.configBancoDados.descricao || ""
        }
      : undefined,
    arquivos: v2.arquivos
      ? {
          id: v2.arquivos.id || "",
          pastaOrigem: v2.arquivos.pastaOrigem || "",
          incluirSubPastas: v2.arquivos.incluirSubPastas !== false,
          pastaDestino: v2.arquivos.pastaDestino || ""
        }
      : undefined,
    batch: v2.batch
      ? {
          id: v2.batch.id || "",
          comando: v2.batch.comando || "",
          pastaDestino: v2.batch.pastaDestino || ""
        }
      : undefined,
    agendamento: v2.agendamento
      ? {
          id: v2.agendamento.id || "",
          tipo: v2.agendamento.tipo || "",
          horaExecucao: v2.agendamento.horaExecucao || ""
        }
      : {
          id: "",
          tipo: v2.tipoAgendamento as any,
          horaExecucao: v2.horaExecucao || ""
        },
    grupo: v2.grupo ? { id: v2.grupo.id || "", nome: v2.grupo.nome || "" } : undefined,
    posExecucao: v2.posExecucao || []
  };
};

const WizardTarefaInterno: React.FC<IWizardTarefaProps> = ({
  tarefaParaEditar,
  onSave,
  onCancel
}) => {
  const [tarefa, setTarefa] = useTarefa();
  const [login] = useLogin();
  const [cliente] = useCliente();
  const [activeStep, setActiveStep] = useState(0);

  // Estados extras do v2 premium
  const [loadingTest, setLoadingTest] = useState(false);
  const [testResult, setTestResult] = useState<{ sucesso: boolean; mensagem: string } | null>(null);
  const [loadingSimulation, setLoadingSimulation] = useState(false);
  const [proximasExecucoes, setProximasExecucoes] = useState<string[]>([]);
  const [validacaoErro, setValidacaoErro] = useState<string | null>(null);

  // Estado do modal de instruções Linux
  const [modalLinuxAberto, setModalLinuxAberto] = useState(false);

  // Reset/Populagem inicial
  useEffect(() => {
    if (tarefaParaEditar) {
      setTarefa(mapearV2ParaTarefa(tarefaParaEditar));
    } else {
      setTarefa(novaTarefa());
    }
    setActiveStep(0);
    setTestResult(null);
    setProximasExecucoes([]);
    setValidacaoErro(null);
  }, [tarefaParaEditar, setTarefa]);

  const handleNext = async () => {
    setValidacaoErro(null);
    const stepObj = forms[activeStep];
    if (!stepObj.validar(tarefa)) {
      setValidacaoErro(`Por favor, preencha os campos obrigatórios do passo de ${stepObj.titulo}.`);
      return;
    }

    if (activeStep < forms.length - 1) {
      setActiveStep((prev) => prev + 1);
      setTestResult(null);
    } else {
      // Salvar Tarefa
      await handleSave();
    }
  };

  const handleBack = () => {
    setValidacaoErro(null);
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
      setTestResult(null);
    }
  };

  const handleSave = async () => {
    try {
      const activeClienteId = cliente.id || tarefa.clienteID || String(login.usuario?.usuarioCliente?.clienteID || "");
      const payload = {
        clienteID: activeClienteId,
        nome: tarefa.nome,
        tipo: tarefa.tipo,
        plataforma: tarefa.plataforma || "windows",
        dataInicio: tarefa.dataInicio,
        dataFim: tarefa.dataFim,
        agendamento: tarefa.agendamento,
        grupo: tarefa.grupo ? { id: tarefa.grupo.id || "", nome: tarefa.grupo.nome } : null,
        configBancoDados: (tarefa.tipo === "SQLServer" || tarefa.tipo === "Postgres" || tarefa.tipo === "Sybase" || tarefa.tipo === "CheckDB") ? tarefa.configBancoDados : null,
        arquivos: tarefa.tipo === "Arquivos" ? tarefa.arquivos : null,
        batch: tarefa.tipo === "Bat" ? tarefa.batch : null,
        posExecucao: tarefa.posExecucao || []
      };

      if (tarefaParaEditar) {
        await TarefasApi.alterarTarefa(tarefaParaEditar.tarefaAgendadaID, payload, login.accessToken);
      } else {
        await TarefasApi.criarTarefa(payload, login.accessToken);
      }

      const ehLinux = (tarefa.plataforma || "windows").toLowerCase() === "linux";
      if (ehLinux) {
        setModalLinuxAberto(true);
      } else {
        onSave();
      }
    } catch (err: any) {
      console.error("Erro ao salvar tarefa agendada", err);
      setValidacaoErro(err.response?.data?.mensagem || "Ocorreu um erro ao salvar as configurações da tarefa.");
    }
  };

  const handleCadastrarOutraLinux = () => {
    setModalLinuxAberto(false);
    // Preserva configurações comuns (Postgres, conexões e grupo) para agilizar cadastro de múltiplas tarefas
    const proxima = novaTarefa();
    proxima.plataforma = "linux";
    proxima.tipo = tarefa.tipo || "Postgres";
    proxima.grupo = tarefa.grupo;
    proxima.configBancoDados = tarefa.configBancoDados ? { ...tarefa.configBancoDados } : undefined;
    proxima.nome = "";
    setTarefa(proxima);
    setActiveStep(0);
    setTestResult(null);
    setProximasExecucoes([]);
    setValidacaoErro(null);
  };

  const handleTestarConexao = async () => {
    setLoadingTest(true);
    setTestResult(null);
    try {
      const payload = {
        tipo: tarefa.tipo,
        configBancoDados: tarefa.configBancoDados,
        arquivos: tarefa.arquivos,
        batch: tarefa.batch
      };
      const res = await TarefasApi.testarConfiguracao(
        tarefaParaEditar?.tarefaAgendadaID || "novo",
        payload,
        login.accessToken
      );
      setTestResult({ sucesso: res.sucesso, mensagem: res.mensagem });
    } catch (err: any) {
      setTestResult({
        sucesso: false,
        mensagem: err.response?.data?.mensagem || "Falha ao realizar teste de comunicação."
      });
    } finally {
      setLoadingTest(false);
    }
  };

  const handleSimularAgendamento = async () => {
    setLoadingSimulation(true);
    setProximasExecucoes([]);
    try {
      const payload = {
        agendamento: tarefa.agendamento,
        dataInicio: tarefa.dataInicio,
        dataFim: tarefa.dataFim
      };
      const res = await TarefasApi.simularAgendamento(payload, login.accessToken);
      setProximasExecucoes(res.proximasExecucoes || []);
    } catch (err: any) {
      console.error("Erro ao simular agendamento", err);
      alert("Erro ao realizar a simulação das próximas execuções.");
    } finally {
      setLoadingSimulation(false);
    }
  };

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
      <Grid item xs={12} md={10} lg={8}>
        <PremiumCard sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: "center" }}>
            {tarefaParaEditar ? `Editar Tarefa: ${tarefaParaEditar.nome}` : "Nova Tarefa de Backup"}
          </Typography>

          <Box sx={{ width: "100%", mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {forms.map((step) => (
                <Step key={step.titulo}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        display: { xs: 'none', sm: 'block' }
                      }
                    }}
                  >
                    {step.titulo}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 4, textAlign: 'center' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Etapa {activeStep + 1} de {forms.length}: {forms[activeStep].titulo}
            </Typography>
          </Box>

          {validacaoErro && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {validacaoErro}
            </Alert>
          )}

          <Box sx={{ mt: 2, minHeight: "260px" }}>
            <Form props={{ onSubmit: (e) => e.preventDefault() }}>
              <Box sx={{ mb: 4 }}>{forms[activeStep].criarForm(tarefa)}</Box>

              {/* Extras do Passo de Configuração/Definição */}
              {activeStep === 3 ? (
                <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleTestarConexao}
                      disabled={loadingTest}
                      startIcon={loadingTest ? <CircularProgress size={20} /> : null}
                    >
                      {loadingTest ? "Testando..." : "Testar Conectividade"}
                    </Button>
                  </Box>
                  {testResult ? (
                    <Alert severity={testResult.sucesso ? "success" : "error"} sx={{ mt: 1 }}>
                      {testResult.mensagem}
                    </Alert>
                  ) : <></>}
                </Box>
              ) : <></>}

              {/* Extras do Passo de Agendamento */}
              {activeStep === 4 ? (
                <Box sx={{ mb: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleSimularAgendamento}
                      disabled={loadingSimulation}
                      startIcon={loadingSimulation ? <CircularProgress size={20} /> : null}
                    >
                      {loadingSimulation ? "Simulando..." : "Simular Próximas Execuções"}
                    </Button>
                  </Box>

                  {proximasExecucoes.length > 0 ? (
                    <Paper sx={{ p: 2, backgroundColor: "rgba(0,0,0,0.01)" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Próximas Execuções Previstas:
                      </Typography>
                      <List dense>
                        {proximasExecucoes.map((dataStr, idx) => (
                          <ListItem key={idx}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <CheckCircleOutlineIcon color="success" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={formatarDataHoraCurta(dataStr)}
                              secondary={`${idx + 1}ª Execução`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  ) : <></>}
                </Box>
              ) : <></>}

              {/* Botões de Navegação */}
              <Box sx={{ display: "flex", justifyContent: "space-between", pt: 2, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={onCancel}
                  sx={{ borderRadius: "8px" }}
                >
                  Cancelar
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    sx={{ borderRadius: "8px" }}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={handleNext}
                    sx={{ borderRadius: "8px" }}
                  >
                    {activeStep === forms.length - 1 ? "Salvar Configurações" : "Próximo"}
                  </Button>
                </Box>
              </Box>
            </Form>
          </Box>

          {/* Modal de Instruções de Instalação Linux (apresentado após cadastro de tarefa Linux) */}
          <ModalInstrucoesLinux
            open={modalLinuxAberto}
            onClose={() => {
              setModalLinuxAberto(false);
              onSave();
            }}
            onCadastrarOutra={handleCadastrarOutraLinux}
            clienteNome={
              (login.usuario?.usuarioCliente as any)?.Cliente?.nomeFantasia ||
              (login.usuario?.usuarioCliente as any)?.cliente?.nomeFantasia ||
              (login.usuario?.usuarioCliente as any)?.Cliente?.razaoSocial ||
              (login.usuario?.usuarioCliente as any)?.cliente?.razaoSocial ||
              "Cliente"
            }
            clienteID={String(cliente.id || tarefa.clienteID || login.usuario?.usuarioCliente?.clienteID || "")}
            token={login.accessToken}
            isPosCadastro={true}
          />
        </PremiumCard>
      </Grid>
    </Grid>
  );
};

export const WizardTarefa: React.FC<IWizardTarefaProps> = (props) => {
  return (
    <TarefaProvider>
      <WizardTarefaInterno {...props} />
    </TarefaProvider>
  );
};
